/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  QuoteResponse,
} from './quote.service.interface';
import { gatewayService } from '../../common/services/gateway'
import { loggerService } from '../../common/services/logger'
import { TokenSetType } from  '../../common/services/auth';

import { Method } from 'axios';
import { isEmpty, pick } from 'lodash/fp';
import { Quote , QuoteIdType, QuoteHashType} from '../../common/interface/quote.interface';
import { QuoteServiceInterface } from './quote.service.interface';

const fieldsWithMerge = [
  'insuredPersons',
  'car',
  'motorcycle',
  'property',
  'applicant',
  'device',
  'insuredHome',
  'certificate',
  'taxInformation',
];

export class QuoteService implements QuoteServiceInterface {
  private async sendRequest<T>(
    method: Method,
    uri: string,
    data?: unknown,
    tokenSet?: TokenSetType,
  ): Promise<T> {
    return gatewayService
      .sendRequest<QuoteResponse<T>>(method, uri, data, tokenSet)
      // @ts-ignores
      .then((response: QuoteResponse<T>) => response.result);
  }

  public async getQuoteById(
    quoteId: string,
    tokenSet?: TokenSetType,
  ): Promise<Quote> {
    try {
      return this.sendRequest<Quote>(
        'GET',
        `/quotes/${quoteId}`,
        undefined,
        tokenSet,
      );
    } catch (error) {
      loggerService.error('Error on getQuoteById:', error);
      throw new Error(`Error on getQuoteById: ${error}`);
    }
  }

  public async getQuoteByHash(
    h: string,
    x: string,
    tokenSet?: TokenSetType,
  ): Promise<Quote> {
    try {
      const { quoteId } = await this.getQuoteIdByHash(h, x, tokenSet);
      return await this.getQuoteById(quoteId, tokenSet);
    } catch (error) {
      loggerService.error('Error on getQuoteByHash:', error);
      throw new Error(`Error on getQuoteByHash: ${error}`);
    }
  }

  public async getQuoteIdByHash(
    h: string,
    x: string,
    tokenSet?: TokenSetType,
  ): Promise<QuoteIdType> {
    try {
      return this.sendRequest(
        'GET',
        `/quote-hashes?h=${h}&x=${x}`,
        undefined,
        tokenSet,
      );
    } catch (error) {
      loggerService.error('Error on getQuoteIdByHash:', error);
      throw new Error(`Error on getQuoteIdByHash: ${error}`);
    }
  }

  public async getQuoteHashById(
    quoteId: string,
    tokenSet?: TokenSetType,
  ): Promise<QuoteHashType> {
    try {
      return this.sendRequest(
        'GET',
        `/quotes/${quoteId}/hashes`,
        undefined,
        tokenSet,
      );
    } catch (error) {
      loggerService.error('Error on getQuoteHashById:', error);
      throw new Error(`Error on getQuoteHashById: ${error}`);
    }
  }

  public createQuote(quote: Quote, tokenSet?: TokenSetType): Promise<Quote> {
    try {
      return this.sendRequest(
        'POST',
        '/quotes',
        quote,
        tokenSet || quote.tokenSet,
      );
    } catch (error) {
      loggerService.error('Error on createQuote:', error);
      throw new Error(`Error on createQuote: ${error}`);
    }
  }

  public async updateQuoteByHash(
    h: string,
    x: string,
    toUpdate: Quote,
    tokenSet?: TokenSetType,
  ): Promise<void> {
    try {
      const { quoteId } = await this.getQuoteIdByHash(h, x, tokenSet);
      await this.updateQuoteById(quoteId, toUpdate, tokenSet);
    } catch (error) {
      loggerService.error('Error on updateQuoteByHash:', error);
      throw new Error(`Error on updateQuoteByHash: ${error}`);
    }
  }

  public async updateQuoteById(
    quoteId: string,
    toUpdate: Quote,
    tokenSet?: TokenSetType,
  ): Promise<Quote> {
    try {
      const toUpdateWithMerge = pick(fieldsWithMerge, toUpdate);

      if (Object.keys(toUpdateWithMerge).length && !toUpdate.planState) {
        const updatedQuote = await this.putWithMerge(
          quoteId,
          toUpdateWithMerge,
          tokenSet,
        );
        fieldsWithMerge.forEach((key) => {
          delete toUpdate[key];
        });
        if (isEmpty(toUpdate)) {
          return updatedQuote;
        }
      }
      return this.sendRequest('PUT', `/quotes/${quoteId}`, toUpdate, tokenSet);
    } catch (error) {
      loggerService.error('Error on updateQuote:', error);
      throw new Error(`Error on updateQuote: ${error}`);
    }
  }

  public putWithMerge(
    quoteId: string,
    toUpdate: Quote,
    tokenSet?: TokenSetType,
  ): Promise<Quote> {
    try {
      return this.sendRequest(
        'PUT',
        `/quotes/${quoteId}`,
        {
          ...toUpdate,
          mergeData: true,
        },
        tokenSet,
      );
    } catch (error) {
      loggerService.error('Error on putWithMerge:', error);
      throw new Error(`Error on putWithMerge: ${error}`);
    }
  }

  public put(
    key: string,
    value: string | object | null,
    quoteId: string,
    tokenSet?: TokenSetType,
  ): Promise<Quote> {
    try {
      const toUpdate = { [key]: value };
      return this.updateQuoteById(quoteId, toUpdate, tokenSet);
    } catch (error) {
      loggerService.error('Error on put:', error);
      throw new Error(`Error on put: ${error}`);
    }
  }

  public async putMany(
    values: Record<string, unknown>,
    quoteId: string,
    tokenSet?: TokenSetType,
  ): Promise<Quote | undefined> {
    try {
      if (!quoteId) return;
      const toUpdate = { ...values };
      return this.updateQuoteById(quoteId, toUpdate, tokenSet);
    } catch (error) {
      loggerService.error('Error on putMany:', error);
      throw new Error(`Error on putMany: ${error}`);
    }
  }

  public duplicateQuote(
    quoteId: string,
    tokenSet?: TokenSetType,
  ): Promise<Quote> {
    try {
      return this.sendRequest(
        'POST',
        '/quotes/duplicate',
        { quoteId },
        tokenSet,
      );
    } catch (error) {
      loggerService.error('Error on duplicateQuote:', error);
      throw new Error(`Error on duplicateQuote: ${error}`);
    }
  }

  public updateExternalReferenceCodeToEntityUserId(
    externalReferenceCode: unknown,
    entityUserId: unknown,
    entityId: unknown,
    tokenSet?: TokenSetType,
  ): Promise<unknown> {
    try {
      return this.sendRequest(
        'POST',
        '/quotes/external-reference-code',
        {
          externalReferenceCode,
          entityUserId,
          entityId,
        },
        tokenSet,
      );
    } catch (error) {
      loggerService.error(
        'Error on updateExternalReferenceCodeToEntityUserId:',
        error,
      );
      throw new Error(
        `Error on updateExternalReferenceCodeToEntityUserId: ${error}`,
      );
    }
  }

  public resetVoucher(
    quoteId: string,
    tokenSet?: TokenSetType,
  ): Promise<unknown> {
    try {
      return this.sendRequest(
        'GET',
        `/quotes/${quoteId}/reset-voucher`,
        undefined,
        tokenSet,
      );
    } catch (error) {
      loggerService.error('Error on resetVoucher:', error);
      throw new Error(`Error on resetVoucher: ${error}`);
    }
  }

  public hasInsuredPolicy(
    quoteId: string,
    tokenSet?: TokenSetType,
  ): Promise<unknown> {
    try {
      return this.sendRequest(
        'GET',
        `/quotes/${quoteId}/validation/insured`,
        undefined,
        tokenSet,
      );
    } catch (error) {
      loggerService.error('Error on hasInsuredPolicy:', error);
      throw new Error(`Error on hasInsuredPolicy: ${error}`);
    }
  }
}

export const quoteService = new QuoteService();
